import React, { useState } from 'react';
import cn from '@/utils/cn';
import { Controller, useForm } from 'react-hook-form';
import LocaleToggle from '@/components/common/LocaleToggle';
import { RequiredAsterisk } from '@/components/common/Forms/Utils';
import GlossaryTermImageUpload from './GlossaryTermImageUpload';

const SPANISH_PLACEHOLDER = 'SPANISH PLACEHOLDER';

const DEFAULT_VALUES = {
  definition_en: '',
  definition_es: '',
  example_sentence_en: '',
  example_sentence_es: '',
  image: null,
  kind: 'Lesson',
  term_en: '',
  term_es: '',
};

type Form = {
  term_en: string;
  term_es: string;
  definition_en: string;
  definition_es: string;
  example_sentence_en: string;
  example_sentence_es: string;
  glossary_image_file_name: string;
  image?: File | null;
  kind: string;
};

interface GlossaryTermProps {
  children: React.ReactNode;
  initialValues?: Partial<Form>;
  onSubmit: (data: FormData) => any;
  sectionId: string;
}

const GlossaryTermForm = ({ children, initialValues = {}, onSubmit, sectionId }: GlossaryTermProps) => {
  const [locale, setLocale] = useState<'en' | 'es'>('en');
  const [imageRemoved, setImageRemoved] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<Form>({
    defaultValues: {
      ...DEFAULT_VALUES,
      ...initialValues,
      example_sentence_en: initialValues.example_sentence_en ?? DEFAULT_VALUES.example_sentence_en,
      example_sentence_es: initialValues.example_sentence_es ?? DEFAULT_VALUES.example_sentence_es,
      kind: initialValues.kind ?? DEFAULT_VALUES.kind,
    },
  });

  const onFormSubmit = (data: Form) => {
    if (!sectionId) return;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const {
      term_en: termEn,
      term_es: termEs,
      definition_en: definitionEn,
      definition_es: definitionEs,
      example_sentence_en: exampleSentenceEn,
      example_sentence_es: exampleSentenceEs,
      image,
      kind,
    } = data;

    const formData = new FormData();
    formData.append('glossary_term[section_id]', sectionId);
    formData.append('glossary_term[term_en]', termEn);
    formData.append('glossary_term[definition_en]', definitionEn);
    formData.append('glossary_term[term_es]', termEs || SPANISH_PLACEHOLDER);
    formData.append('glossary_term[definition_es]', definitionEs || SPANISH_PLACEHOLDER);
    formData.append('glossary_term[example_sentence_en]', exampleSentenceEn);
    formData.append('glossary_term[example_sentence_es]', exampleSentenceEs || SPANISH_PLACEHOLDER);
    formData.append('glossary_term[kind]', kind);
    if (image) formData.append('glossary_term[glossary_image]', image);
    else if (imageRemoved) formData.append('glossary_term[remove_glossary_image]', 'true');

    onSubmit(formData);

    reset({
      definition_en: '',
      definition_es: '',
      example_sentence_en: '',
      example_sentence_es: '',
      image: null,
      kind: 'Lesson',
      term_en: '',
      term_es: '',
    });
    setImageRemoved(false);
  };

  return (
    <div>
      <LocaleToggle
        btnClasses="tw-block tw-mb-[1.5rem] tw-overflow-hidden tw-text-tci-purple"
        isSaving={false}
        locale={locale}
        setLocale={setLocale}
      />
      <form className="tw-flex tw-flex-col" onSubmit={handleSubmit(onFormSubmit)}>
        {/* Term */}
        <label className="tw-flex tw-mb-[1.5rem]">
          <div className="tw-flex tw-mr-[20px] tw-w-[6rem] tw-pt-[0.5rem]">
            <p className="tw-font-bold tw-text-[16px]"> Term </p>
            {locale === 'en' && <RequiredAsterisk />}
          </div>
          <input
            type="text"
            {...register('term_en', { required: true })}
            className={cn('tw-w-full tw-rounded-lg tw-h-[2.5rem] tw-px-[1rem] tw-border-[1px] tw-border-solid', {
              'tw-border-grey': !errors.term_en,
              'tw-border-red': errors.term_en,
              'tw-hidden': locale === 'es',
            })}
          />
          <input
            type="text"
            {...register('term_es')}
            className={cn('tw-w-full tw-rounded-lg tw-h-[2.5rem] tw-px-[1rem] tw-border-[1px] tw-border-solid', {
              'tw-border-grey': !errors.term_es,
              'tw-border-solid tw-border-red': errors.term_es,
              'tw-hidden': locale === 'en',
            })}
          />
        </label>
        {/* Definition */}
        <label className="tw-flex tw-mb-[1.5rem]">
          <div className="tw-flex tw-mr-[20px] tw-w-[6rem] tw-pt-[0.5rem]">
            <p className="tw-font-bold tw-text-[16px]"> Definition </p>
            {locale === 'en' && <RequiredAsterisk />}
          </div>
          <textarea
            {...register('definition_en', { required: true })}
            className={cn(
              'tw-w-full tw-resize-none tw-rounded-lg tw-h-[5rem] tw-pt-2 tw-px-[1rem]',
              'tw-border-[1px] tw-border-solid', {
                'tw-border-grey': !errors.definition_en,
                'tw-border-red': errors.definition_en,
                'tw-hidden': locale === 'es',
              },
            )}
          />
          <textarea
            {...register('definition_es')}
            className={cn(
              'tw-w-full tw-resize-none tw-rounded-lg tw-h-[5rem] tw-pt-2 tw-px-[1rem]',
              'tw-border-[1px] tw-border-solid', {
                'tw-border-grey': !errors.definition_es,
                'tw-border-red': errors.definition_es,
                'tw-hidden': locale === 'en',
              },
            )}
          />
        </label>
        {/* Image */}
        <div className="tw-flex tw-mb-[1.5rem]">
          <p className="tw-font-bold tw-w-[6rem] tw-text-[16px] tw-mr-1"> Image </p>
          <Controller
            control={control}
            name="image"
            render={({ field }) => (
              <GlossaryTermImageUpload
                field={field}
                glossary_image_file_name={initialValues.glossary_image_file_name}
                onImageRemoved={setImageRemoved}
              />
            )}
          />
        </div>
        {/* Type */}
        <label className="tw-flex tw-mb-[1.5rem]">
          <div className="tw-flex tw-mr-[5px] tw-w-[6rem] tw-pt-[0.5rem]">
            <p className="tw-font-bold tw-text-[16px]"> Type </p>
            <RequiredAsterisk />
          </div>
          <select
            {...register('kind', { required: true })}
            className={cn('tw-rounded-lg tw-p-2 tw-w-32', {
              'tw-border-grey': !errors.kind,
              'tw-border-red': errors.kind,
            })}
          >
            <option className="tw-m-1" value="Lesson">Lesson</option>
            <option className="tw-m-1" value="Academic">Academic</option>
          </select>
        </label>
        {/* Example Sentence */}
        <label className="tw-flex tw-mb-[1.5rem]">
          <div className="tw-flex tw-mr-[20px] tw-w-[6rem] tw-pt-[0.5rem]">
            <p className="tw-font-bold tw-text-[16px]"> Example sentence </p>
          </div>
          <textarea
            {...register('example_sentence_en')}
            className={cn('tw-w-full tw-resize-none tw-rounded-lg tw-h-[5rem] tw-pt-2 tw-px-[1rem]', {
              'tw-border-grey': !errors.example_sentence_en,
              'tw-border-red': errors.example_sentence_en,
              'tw-hidden': locale === 'es',
            })}
          />
          <textarea
            {...register('example_sentence_es')}
            className={cn('tw-w-full tw-resize-none tw-rounded-lg tw-h-[5rem] tw-pt-2 tw-px-[1rem]', {
              'tw-border-grey': !errors.example_sentence_es,
              'tw-border-solid tw-border-red': errors.example_sentence_es,
              'tw-hidden': locale === 'en',
            })}
          />
        </label>
        {children}
        {Object.keys(errors).length > 0 && (
          <p className="tw-text-red tw-absolute tw-bottom-4">Please double-check the English fields.</p>
        )}
      </form>
    </div>
  );
};

export default GlossaryTermForm;
